/**
 * /programs/:id/preview/days — gradient-shell card with one PreviewTile
 * per program day. First day defaults to "open" (cosmetic for the trainer);
 * every day open for author review. Clicking a tile drills into the day.
 */

import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useProgram } from '../../services/programs';
import { useImageUrl } from '../../services/uploads';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import PreviewPageShell from '../../components/programs/preview/PreviewPageShell';

export default function ProgramPreviewDaysPage() {
  const { id } = useParams<{ id: string }>();
  const { data: program, isLoading, isError } = useProgram(id);

  if (isLoading) {
    return (
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="h-12 w-1/2 rounded-lg bg-text-tertiary/10 animate-pulse" />
        <div className="h-72 rounded-[28px] bg-text-tertiary/10 animate-pulse" />
      </div>
    );
  }
  if (isError || !program || !id) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">Could not load the program preview.</p>
        <Link to="/programs" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Programs
        </Link>
      </div>
    );
  }

  const sortedDays = program.days.slice().sort((a, b) => a.day_number - b.day_number);

  return (
    <PreviewPageShell
      programId={id}
      upHref={`/programs/${id}/preview`}
      upLabel="Back to Welcome"
      programIsActive={program.status === 'active'}
    >
      <h2 className="text-3xl sm:text-4xl font-extrabold text-primary-500 px-1 sm:px-2 leading-tight">
        Welcome To {program.name}!
      </h2>

      {sortedDays.length === 0 ? (
        <p className="text-sm text-text-secondary text-center py-12">
          This program doesn&apos;t have any days configured yet.
        </p>
      ) : (
        <PreviewTileGrid maxColumns={4}>
          {sortedDays.map((day, i) => (
            <DayTileCell key={day.id} programId={id} day={day} index={i} />
          ))}
        </PreviewTileGrid>
      )}
    </PreviewPageShell>
  );
}

interface DayTileCellProps {
  programId: string;
  day: { id: string; day_number: number; title: string; header_image: string | null; courses_count: number };
  index: number;
}

function DayTileCell({ programId, day, index }: DayTileCellProps) {
  const navigate = useNavigate();
  const [headerUrl] = useImageUrl(day.header_image);
  // Author review: every day is open. Learner-side sequential locking is a
  // runtime behavior — faking it here only blocked the trainer (and the
  // Next-day chain rightly ignored it, making the two paths disagree).
  return (
    <PreviewTile
      imageUrl={headerUrl ?? undefined}
      label={day.title || `Day ${day.day_number}`}
      state="open"
      entranceDelay={index * 0.05}
      onClick={() => navigate(`/programs/${programId}/preview/days/${day.id}`)}
      aspectClassName="aspect-[4/5]"
    />
  );
}
